import $http from '@/plugins/axios'

const DB_NAME = 'ContentfulCache'
const STORAGE_NAME = 'Entries'
const DB_VERSION = 1 // Should always be 1?
const HOUR = 1000 * 60 * 60

const openDB = () => {
  const res = indexedDB.open(DB_NAME, DB_VERSION)
  res.onupgradeneeded = (e) => {
    const db = e.target.result
    db.createObjectStore(STORAGE_NAME)
  }
  return res
}

export const pullFromCache = (key) => {
  return new Promise((resolve, reject) => {
    const request = openDB()

    request.onsuccess = (e) => {
      const db = e.target.result
      const transaction = db.transaction([STORAGE_NAME], 'readonly')
      const store = transaction.objectStore(STORAGE_NAME)

      const getRequest = store.get(key)

      getRequest.onsuccess = () => {
        const cacheResult = getRequest.result

        // ealry exit if undefined (We have never cached this entry before.)
        if (!cacheResult) {
          resolve(cacheResult)
          return
        }

        const cachedAt = cacheResult.cachedAt
        const timeCached = Date.now() - cachedAt

        /*
        * Return undefined if cache has met our timeout. This will lead to another API call that will update this cache value and timed cache
        * (AKA: will just call cacheEntry again)
        */
        if (timeCached > HOUR) resolve(undefined)
        else resolve(getRequest.result) // Return our cached data
      }

      getRequest.onerror = () => {
        reject(getRequest.error)
      }
    }

    request.onerror = () => {
      reject(request.error)
    }
  })
}

// Key should be the contenful ID.
export const cacheEntry = (key, data) => {
  const request = openDB()

  // Mark when we cache. This will expire after some time.
  data.cachedAt = Date.now()

  request.onsuccess = (e) => {
    const db = e.target.result
    const transaction = db.transaction([STORAGE_NAME], 'readwrite')
    const store = transaction.objectStore(STORAGE_NAME)

    store.put(data, key)

    transaction.onerror = () => {
      console.error('Error saving data:', transaction.error)
    }
  }
}

const clearObjectStore = () => {
  const request = openDB()
  request.onsuccess = (e) => {
    const db = e.target.result
    const transaction = db.transaction([STORAGE_NAME], 'readwrite')
    const store = transaction.objectStore(STORAGE_NAME)
    const clearRequest = store.clear()

    clearRequest.onerror = function () {
      console.error('Error clearing object store')
    }
  }
}

/*
* A check to see if we should dump the current contentful cache on the user's machine.
*
* Why?
* - Allows for users to pull new contentful data if a new change has been made in the contentful CMS.
*
* Condition?
* - If any content in contentful is updated, we will force dump the cache.
*
* Where is this called?
* - Called in page.vue. Should be called once per page load.
*/
export const forceCacheDump = async () => {
  // Pull the most recently updated contentful item (will be any item. type does not matter)
  const res = await $http().get(buildQuery())

  // Check the time that this was updated
  const lastUpdatedItem = res.items[0]
  const itemUpdatedAt = lastUpdatedItem?.sys?.updatedAt
  const d = new Date(itemUpdatedAt)

  const millis = d.getTime()
  // User's last update stamp
  const lastStamp = getLastUpdatedStamp()

  // A change has been published to contenful. Dump cache and force new API pulls.
  if (lastStamp !== millis) {
    saveLastUpdateStamp(millis)
    clearObjectStore()
  }
}

const getLastUpdatedStamp = () => {
  let stamp = 0
  if (localStorage.lastContentUpdate) {
    try {
      stamp = JSON.parse(localStorage.lastContentUpdate)
    } catch (e) {
      console.log(`Failed to parse lastContentUpdate (defaulting to 0. Will force cache dump): ${e}`)
    }
  }
  return stamp
}

const saveLastUpdateStamp = (stamp) => {
  localStorage.lastContentUpdate = stamp
}

// Just taking these from contentful.js
const getUrl = () => {
  const inPreviewMode = isPreviewMode()
  return inPreviewMode ? process.env.VUE_APP_CTF_API_PREVIEW_URL : process.env.VUE_APP_CTF_API_URL
}

const getToken = () => {
  const inPreviewMode = isPreviewMode()
  return inPreviewMode ? process.env.VUE_APP_CTF_PREVIEW_TOKEN : process.env.VUE_APP_CTF_TOKEN
}

const isPreviewMode = () => {
  let preview = false
  if (localStorage.contentPreview) {
    try {
      preview = JSON.parse(localStorage.contentPreview)
    } catch (e) {
      console.log(`Failed to parse contentPreview (defaulting to published (live site)): ${e}`)
    }
  }

  return preview
}

const buildQuery = () => {
  return getUrl() + `/spaces/${process.env.VUE_APP_CTF_SPACE}/environments/${process.env.VUE_APP_CTF_ENV}/entries?access_token=${getToken()}&order=-sys.updatedAt&limit=1`
}
